import type {
  BrandCheck,
  BrandSetCheck,
  FilterCheck,
  SegmentCheck,
} from "@/interfaces/selections/selections.types";

export interface ValidateViewsResponse {
  brands?: BrandCheck[];
  brandSets?: BrandSetCheck[];
  filters?: FilterCheck[];
  segments?: SegmentCheck[];
}

export interface FormattedValidationState {
  brand?: string[];
  brandSet?: string[];
  filter?: string[];
  segment?: string[];
}

/** Statuses that mean the user already has access — not shown in the warning modal. */
const ACCESSIBLE_STATUSES = new Set(["OWNED", "ACCESSIBLE", "ALREADY_HAVE", "ELIGIBLE"]);

export function getFormattedValidationState(
  data: ValidateViewsResponse,
): FormattedValidationState | null {
  const result: FormattedValidationState = {};

  // Brands: show brands where accessible === false
  if (data?.brands) {
    const inaccessibleBrands = data.brands
      .filter((b) => !b.accessible && b.name)
      .map((b) => b.name as string);
    if (inaccessibleBrands.length > 0) {
      result.brand = inaccessibleBrands;
    }
  }

  // Brand Sets: show brand sets that are not accessible (status NOT_ELIGIBLE).
  // OWNED / ACCESSIBLE / ALREADY_HAVE / ELIGIBLE all mean the user can still use the set.
  if (data?.brandSets) {
    const inaccessibleBrandSets = data.brandSets
      .filter((bs) => !ACCESSIBLE_STATUSES.has(bs.status))
      .map((bs) => bs.name ?? bs.ulid);
    if (inaccessibleBrandSets.length > 0) {
      result.brandSet = inaccessibleBrandSets;
    }
  }

  // Filters: show filters where accessible === false, or options where accessible === false
  if (data?.filters) {
    const inaccessibleFilters: string[] = [];
    data.filters.forEach((f) => {
      if (!f.accessible && f.label) {
        inaccessibleFilters.push(f.label);
      }
      f.options?.forEach((opt) => {
        if (!opt.accessible && opt.label) {
          inaccessibleFilters.push(opt.label);
        }
      });
    });
    if (inaccessibleFilters.length > 0) {
      result.filter = inaccessibleFilters;
    }
  }

  // Segments: show segments that are not accessible (status NOT_ELIGIBLE)
  if (data?.segments) {
    const inaccessibleSegments = data.segments
      .filter((s) => !ACCESSIBLE_STATUSES.has(s.status) && s.label)
      .map((s) => s.label as string);
    if (inaccessibleSegments.length > 0) {
      result.segment = inaccessibleSegments;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}
